/** @jsx jsx */
import { Grid, Icon, IconButton, List } from "@cd2/components"
import { P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import { SiteBanner } from "../Pages/SiteBanners/SiteBanner"
import { TasksSummaryPopup } from "../Pages/TasksPage"
import { icons } from "../constants/icons"
import { useCommonBreakpoints } from "../hooks/useCommonBreakPoints"
import { eRoutes } from "../routes"
import { brandColors, colors, themeColors } from "./EOSThemeProvider"
import { useGlobalState } from "./GlobalStateProvider"
import { FortusLogo } from "./Logo"
import { MultiBranchSwitcher } from "./MultiBranchSwitcher"
import { NotificationsBell } from "./NotificationsBell"
import { SageImportButton } from "./SageImportButton"
import { Sidebar, SIDEBAR_WIDTH } from "./Sidebar"
import { StockLookup } from "./StockLookup"
import { VersionTokenCheck } from "./VersionTokenCheck"
import { useFetchData } from "../apiRequests/utils"
import { getBranch } from "../apiRequests/branch"
import { formatDistanceToNowStrict } from "date-fns"
import { VideoWatcher } from "../Pages/SystemVideosIndexPage"
import { Flex } from "./Flex"
import { GlobalSearchPopup } from "./GlobalSearchPopup"
import { LinkTooltipsEditModeBanner, LinkTooltipsProvider } from "./LinkTooltips"

interface IProps {
  children: React.ReactNode
}

export const APP_LEVEL_TASK_BAR = "app_level_task_bar"
export const HEADER_HEIGHT = 56

export function SiteWrapper(props: IProps) {
  return (
    <LinkTooltipsProvider>
      <SiteWrapperContent {...props} />
      <LinkTooltipsEditModeBanner />
    </LinkTooltipsProvider>
  )
}

function SiteWrapperContent({ children }: IProps) {
  const { isSmall } = useCommonBreakpoints()
  const [isSidebarControlOpen, setIsSidebarControlOpen] = useState(false)
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false)
  const { currentUser } = useGlobalState()
  const { data: branch } = useFetchData(
    () => getBranch(currentUser.branch.id!),
    currentUser.branch.id,
  )

  const isSidebarClosed = !isSidebarControlOpen && isSmall
  const isMaskPresent = isSidebarControlOpen && isSmall

  const canViewGlobalSearch = currentUser.canEditGlobalUserPermissions

  return (
    <div className="eos-site-wrapper">
      <div
        css={css`
          height: ${HEADER_HEIGHT * (isSmall ? 2 : 1)}px;
        `}
      />
      <VideoWatcher />
      <Link
        css={css`
          position: fixed;
          bottom: 10px;
          right: 10px;
          height: 40px;
          width: 180px;
          padding: 10px 16px;
          background-color: ${themeColors.darkestBlue};
          color: white;
          z-index: 999;
          border-radius: 20px;
          text-align: center;
          line-height: 18px;
          cursor: pointer;
          font-size: 20px;
          box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: space-between;
        `}
        to={eRoutes["/internal-tickets"]}
        className="no-screenshot"
      >
        <div
          css={css`
            font-size: 14px;
          `}
        >
          Help / Feedback
        </div>
        <div>
          <Icon icon={icons.internalTickets} />
        </div>
      </Link>
      <div
        css={css`
          background-color: ${brandColors.night};
          height: ${HEADER_HEIGHT}px;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 12;
        `}
      >
        <List justifyContent="space-between" horizontal>
          <Grid
            css={css`
              width: 100%;
            `}
            columns={
              isSmall
                ? [{ fr: 1 }, "max-content", "max-content"]
                : [SIDEBAR_WIDTH, { fr: 1 }, "max-content", "max-content"]
            }
            alignItems="center"
          >
            {!isSmall && (
              <div
                css={css`
                  background: #1b1b1b;
                  height: ${HEADER_HEIGHT + 2}px;
                  padding: 18px 12px;
                  text-align: center;
                `}
              >
                <div
                  css={css`
                    max-width: 130px;
                    margin: 0 auto;
                  `}
                >
                  <Link to="/">
                    <FortusLogo />
                  </Link>
                </div>
              </div>
            )}

            {currentUser.userAccountType !== "external" ? (
              <Grid
                css={css`
                  padding: 8px ${isSmall ? 6 : 12}px;
                  width: 100%;
                `}
                columns={isSmall ? ["max-content", "max-content"] : ["max-content", { fr: 1 }, 155]}
                gap={isSmall ? 6 : 12}
              >
                {isSmall && (
                  <div>
                    <IconButton
                      icon="bars"
                      padding={12}
                      iconSize={14}
                      buttonStyle="text"
                      buttonColor="#999"
                      onClick={() => setIsSidebarControlOpen(!isSidebarControlOpen)}
                      color="white"
                      square
                    />
                  </div>
                )}
                <div>{currentUser.canRunSageImports && <SageImportButton compact={isSmall} />}</div>
                {!isSmall && (
                  <div>
                    <StockLookup />
                  </div>
                )}
              </Grid>
            ) : (
              <Grid
                css={css`
                  padding: 8px ${isSmall ? 6 : 12}px;
                  width: 100%;
                `}
                columns={["max-content", "auto", "max-content"]}
                gap={isSmall ? 6 : 12}
                alignItems="center"
              >
                <div>
                  {isSmall ? (
                    <IconButton
                      icon={isSidebarControlOpen ? icons.cross : icons.menu}
                      padding={12}
                      iconSize={14}
                      buttonStyle="text"
                      buttonColor="#999"
                      onClick={() => setIsSidebarControlOpen(!isSidebarControlOpen)}
                      square
                    />
                  ) : null}
                </div>
                <div
                  css={css`
                    max-width: 130px;
                    margin: 0 auto;
                  `}
                >
                  {isSmall ? <FortusLogo /> : null}
                </div>
                <div>
                  <div
                    css={css`
                      border-radius: 360px;
                      background-color: #606060;
                      width: 40px;
                      height: 40px;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                    `}
                  >
                    <P
                      color="white"
                      blockSpacing={0}
                      css={css`
                        width: max-content;
                        height: max-content;
                      `}
                    >
                      {/* {nameInitials(currentUser.name)} */}
                    </P>
                  </div>
                </div>
              </Grid>
            )}
            <Grid
              css={css`
                padding-right: 16px;
              `}
              columns={
                canViewGlobalSearch
                  ? ["max-content", "max-content", "max-content", "max-content"]
                  : ["max-content", "max-content", "max-content"]
              }
              gap={8}
            >
              {canViewGlobalSearch && (
                <IconButton
                  icon={icons.search}
                  color={"white"}
                  buttonStyle={"text"}
                  onClick={() => setIsGlobalSearchOpen(true)}
                  square
                />
              )}
              <Link to={eRoutes["/change-log"]}>
                <IconButton icon={icons.changelog} color={"white"} buttonStyle={"text"} square />
              </Link>
              <NotificationsBell />
              <TasksSummaryPopup />
            </Grid>
          </Grid>
        </List>
      </div>
      {currentUser.userAccountType !== "external" && isSmall && (
        <div
          css={css`
            background-color: ${brandColors.night};
            width: 100%;
            padding: 4px 6px;
            height: ${HEADER_HEIGHT}px;
            position: fixed;
            top: ${HEADER_HEIGHT}px;
            left: 0;
            z-index: 12;
          `}
        >
          <StockLookup />
        </div>
      )}
      <div
        css={css`
          min-height: calc(100vh - 68px);
        `}
      >
        <div
          css={css`
            position: fixed;
            z-index: 11;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            pointer-events: ${isMaskPresent ? "all" : "none"};
            background: rgba(0, 0, 0, 0.2);
            cursor: pointer;
            opacity: ${isMaskPresent ? 1 : 0};
            transition: opacity 0.3s;
          `}
          onClick={() => setIsSidebarControlOpen(false)}
        />
        <div
          css={css`
            padding-left: ${isSmall ? 0 : SIDEBAR_WIDTH}px;
            z-index: 12;
          `}
        >
          <Sidebar
            css={css`
              transform: translateX(${isSidebarClosed ? `-${SIDEBAR_WIDTH}` : 0}px);
              transition: 0.2s;
            `}
            onLinkClick={() => setIsSidebarControlOpen(false)}
          />
          <div
            className="eos-site-wrapper-content"
            css={css`
              position: relative;
            `}
          >
            <List>
              <div>
                <Grid
                  columns={["auto", "max-content"]}
                  css={css`
                    background-color: ${brandColors.darkMint};
                    padding: 8px 16px;
                    border-bottom: 1px solid ${colors.lightBorder};
                  `}
                  gap={16}
                  alignItems="center"
                >
                  <Flex gap={8}>
                    <MultiBranchSwitcher />
                  </Flex>
                  <div>
                    <div>
                      <P color="white" blockSpacing={0}>
                        Last Import:{" "}
                        {branch?.lastImportAt
                          ? formatDistanceToNowStrict(new Date(branch?.lastImportAt), {
                              addSuffix: true,
                            })
                          : "-"}
                      </P>
                    </div>
                    <div>
                      <P color="white" blockSpacing={0}>
                        Last Export:{" "}
                        {branch?.lastOrderExportAt
                          ? formatDistanceToNowStrict(new Date(branch.lastOrderExportAt), {
                              addSuffix: true,
                            })
                          : "-"}
                      </P>
                    </div>
                  </div>
                </Grid>
                <SiteBanner />
                {children}
                <div
                  id={APP_LEVEL_TASK_BAR}
                  css={css`
                    position: fixed;
                    right: 0;
                    top: ${HEADER_HEIGHT}px;
                    z-index: 98;
                  `}
                ></div>
              </div>
            </List>
            <VersionTokenCheck />
          </div>
        </div>
      </div>
      {canViewGlobalSearch && (
        <GlobalSearchPopup
          isOpen={isGlobalSearchOpen}
          onRequestClose={() => setIsGlobalSearchOpen(false)}
        />
      )}
    </div>
  )
}
